import { ReactNode } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { useDeleteModal } from '@components/generic-modals';
import { IconTextFormatLinkOff } from '@components/icons/text-format-link-off';
import { forceHideModal } from '@components/modal/modal';
import { useRotatingLoadingMessages } from '@components/rotating-loading-messages';
import { forceHideSidebar, useSidebarState } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { useRemoveJourneyAction } from 'api/journey-actions/hooks';
import { listJourneyActionDependants } from 'api/journey-actions/requests';
import { BASE_ACTION_TYPE } from 'api/types';

import { useNodes } from './use-nodes';

import { JourneyActionBasic, GenericActionCard, nodeToActionBasic } from '../node-types/common';
import { JourneyNode } from '../node-types/types';

const variableLocale = {
  en: {
    confirmDescriptionEmpty: 'Are you sure you want to delete this action?',
    confirmDescription:
      'Moving this action will break connected values between the actions below.\n\nAre you sure you want to delete this action?',
    confirmActionLabel: 'Connected actions',
    removingAction: 'Removing...',
  },
};

type UseDeleteJourneyActionOptions = {
  /**
   * Custom function to render breaking connections.
   * If not provided, uses default rendering with GenericActionCard.
   */
  renderBreakingConnections?: (
    dependants: [JourneyNode<BASE_ACTION_TYPE>, JourneyNode<BASE_ACTION_TYPE>][]
  ) => ReactNode;

  /**
   * Custom function to check if sidebar should be closed.
   * If not provided, uses default check against sidebarActiveId.
   */
  shouldCloseSidebar?: (actionId: string) => boolean;

  /**
   * Called after successful deletion, before sidebar/modal are closed.
   */
  onDeleteSuccess?: () => void;
};

/**
 * Shared hook for deleting journey actions with dependants checking.
 *
 * Handles:
 * - Fetching action dependants
 * - Showing appropriate modal (regular or large) based on dependants count
 * - Rendering breaking connections visualization
 * - Removing the action
 * - Closing sidebar/modal if needed
 *
 * @returns deleteAction function that can be called with a JourneyActionBasic
 */
export const useDeleteJourneyAction = (options: UseDeleteJourneyActionOptions = {}) => {
  const { t } = useScopedTranslate(variableLocale);
  const { getValidNodes } = useNodes();
  const { activeId: sidebarActiveId } = useSidebarState();
  const showDeleteModalRegular = useDeleteModal();
  const showDeleteModalLarge = useDeleteModal('LARGE');
  const removeMutation = useRemoveJourneyAction();
  const queryClient = useQueryClient();

  useRotatingLoadingMessages({
    isEnabled: removeMutation.isLoading,
    firstMessage: t('removingAction'),
  });

  const deleteAction = async (action: JourneyActionBasic): Promise<void> => {
    const handleRemove = async () => {
      forceHideModal();
      await removeMutation.mutateAsync(action.shortId);

      options.onDeleteSuccess?.();

      const shouldClose = options.shouldCloseSidebar
        ? options.shouldCloseSidebar(action.shortId)
        : sidebarActiveId === action.shortId;

      if (shouldClose) {
        requestAnimationFrame(() => {
          forceHideSidebar();
        });
      }
    };

    if (action.action === 'placeholder') {
      await handleRemove();
      return;
    }

    const dependants = await queryClient.fetchQuery({
      queryKey: ['journey', action.shortId, 'actions', 'dependants'],
      queryFn: () => listJourneyActionDependants(action.shortId),
    });
    const dependantsCount = dependants?.length ?? 0;

    const showDeleteModal = dependantsCount > 0 ? showDeleteModalLarge : showDeleteModalRegular;
    const modalDescription =
      dependantsCount === 0 ? t('confirmDescriptionEmpty') : t('confirmDescription');

    const nodes = getValidNodes();
    const confirmationNodes = dependants?.map(([parentNodeId, childNodeId]) => {
      const parentNode = nodes.find((node) => node.id === parentNodeId);
      const childNode = nodes.find((node) => node.id === childNodeId);
      return [parentNode, childNode];
    }) as [JourneyNode<BASE_ACTION_TYPE>, JourneyNode<BASE_ACTION_TYPE>][];

    const breakingConnectionsContent = options.renderBreakingConnections
      ? options.renderBreakingConnections(confirmationNodes)
      : renderDefaultBreakingConnections(confirmationNodes, t('confirmActionLabel'));

    showDeleteModal({
      description: modalDescription,
      onConfirm: handleRemove,
      children: dependantsCount === 0 ? null : breakingConnectionsContent,
    });
  };

  return {
    deleteAction,
    isDeleting: removeMutation.isLoading,
  };
};

/**
 * Default rendering for breaking connections visualization
 */
const renderDefaultBreakingConnections = (
  confirmationNodes: [JourneyNode<BASE_ACTION_TYPE>, JourneyNode<BASE_ACTION_TYPE>][],
  label: string
) => {
  return (
    <Stack space={2}>
      <Text font="semibold14">{label}</Text>
      {confirmationNodes?.map(([parentNode, childNode]) => (
        <Stack
          key={`${parentNode.id}-${childNode.id}`}
          orientation="horizontal"
          justify="space-between"
          align="center"
          space={1}
        >
          <GenericActionCard action={nodeToActionBasic(parentNode)} />
          <IconTextFormatLinkOff color="elementRed100" />
          <GenericActionCard action={nodeToActionBasic(childNode)} />
        </Stack>
      ))}
    </Stack>
  );
};
